import { useState } from 'react';
import styled from 'styled-components';
import { IconAlertTriangle, IconCheck } from '@u21/tabler-icons';
import { U21Alert } from 'app/shared/u21-ui/components/display/U21Alert';
import { U21Chip } from 'app/shared/u21-ui/components/display/U21Chip';
import { U21Loading } from 'app/shared/u21-ui/components/display/U21Loading';
import { U21Modal } from 'app/shared/u21-ui/components/layout/modal/U21Modal';

const ICON_SIZE = 16;

interface UploadingStatus {
  state: 'uploading';
}

interface SuccessStatus {
  embedUrl?: string;
  mimeType?: string;
  state: 'success';
}

interface FailedStatus {
  message?: string;
  state: 'failed';
}

export type U21AttachmentCardStatus =
  | FailedStatus
  | SuccessStatus
  | UploadingStatus;

export interface U21AttachmentCardProps {
  fileName: string;
  onRemove?: () => void;
  status: U21AttachmentCardStatus;
}

const getStartIcon = (status: U21AttachmentCardStatus) => {
  switch (status.state) {
    case 'uploading':
      return (
        <U21Loading delay={0} loading size={ICON_SIZE} variant="spinner" />
      );
    case 'success':
      return <SuccessIcon aria-hidden />;
    case 'failed':
      return <FailedIcon aria-hidden />;
    default:
      return undefined;
  }
};

const getTooltip = (status: U21AttachmentCardStatus) => {
  if (status.state === 'failed') {
    return status.message ?? 'Upload failed';
  }
  return undefined;
};

const canPreviewMime = (mime?: string): mime is string => {
  if (!mime) {
    return false;
  }
  return (
    mime.startsWith('image/') ||
    mime === 'application/pdf' ||
    mime.startsWith('video/')
  );
};

export const U21AiChatAttachmentChip = ({
  fileName,
  onRemove,
  status,
}: U21AttachmentCardProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  const embedUrl = status.state === 'success' ? status.embedUrl : undefined;
  const mimeType = status.state === 'success' ? status.mimeType : undefined;
  const canPreview = embedUrl !== undefined;

  return (
    <>
      <StyledU21Chip
        icon={getStartIcon(status)}
        onClick={canPreview ? () => setPreviewOpen(true) : undefined}
        onDelete={onRemove}
        tooltip={getTooltip(status)}
        variant="filled"
      >
        {fileName}
      </StyledU21Chip>
      {canPreview && (
        <U21Modal
          actionButtonProps={{
            children: 'Download',
          }}
          closeButtonProps={{ children: 'Close' }}
          disableTitleCase
          onAction={() => {
            window.open(embedUrl, '_blank', 'noopener,noreferrer');
            setPreviewOpen(false);
          }}
          onClose={() => setPreviewOpen(false)}
          open={previewOpen}
          size={canPreviewMime(mimeType) ? 'large' : 'medium'}
          title={fileName}
        >
          {canPreviewMime(mimeType) ? (
            <PreviewContainer>
              {mimeType.startsWith('image/') && (
                <PreviewImage alt={fileName} src={embedUrl} />
              )}
              {mimeType === 'application/pdf' && (
                <PreviewIframe src={embedUrl} title={fileName} />
              )}
              {mimeType.startsWith('video/') && (
                <PreviewVideo controls src={embedUrl}>
                  <track kind="captions" />
                </PreviewVideo>
              )}
            </PreviewContainer>
          ) : (
            <U21Alert color="info">
              Preview not available for this file type. Download this file
              instead.
            </U21Alert>
          )}
        </U21Modal>
      )}
    </>
  );
};

const StyledU21Chip = styled(U21Chip)`
  height: ${({ theme }) => theme.spacing(4)};
  max-width: ${({ theme }) => theme.spacing(50)};

  // Use an opaque palette surface instead of the default translucent chip
  // background so the chip doesn't blend with colored message bubbles.
  background-color: ${({ theme }) => theme.palette.background.paper};
  color: ${({ theme }) => theme.palette.text.primary};

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }
`;

const SuccessIcon = styled(IconCheck)`
  // overwrite default U21Chip icon color
  &&& {
    color: ${({ theme }) => theme.palette.success.main};
  }
`;

const FailedIcon = styled(IconAlertTriangle)`
  // overwrite default U21Chip icon color
  &&& {
    color: ${({ theme }) => theme.palette.warning.main};
  }
`;

const PreviewContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  max-height: 70vh;
  min-height: 320px;
  overflow: auto;
`;

const PreviewImage = styled.img`
  max-height: 70vh;
  max-width: 100%;
  object-fit: contain;
`;

const PreviewIframe = styled.iframe`
  border: none;
  min-height: 70vh;
  width: 100%;
`;

const PreviewVideo = styled.video`
  max-height: 70vh;
  max-width: 100%;
`;
